import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { toast } from '@gitbook/spine-react';
import { useEventCallback, useLoadingState } from '@gitbook/util-react-hooks';

import { apiClient, apiQuery } from '../../../../state';

type SiteSettingsContextValue = {
    siteSettings: api.SiteCustomizationSettings;
    savingSiteSettings: boolean;
    updateSiteSettings: (
        input: Partial<api.SiteCustomizationSettings>,
        toastInfos: { title: string }
    ) => Promise<void>;
};

const SiteSettingsContext = React.createContext<SiteSettingsContextValue | null>(null);

export function SiteSettingsProvider(props: {
    children: React.ReactNode;
    organizationId: string;
    site: api.Site;
}) {
    const { organizationId, site, children } = props;

    const { data: siteSettings } = useSuspenseQuery(
        apiQuery('getSiteRawCustomizationById', { organizationId, siteId: site.id })
    );

    const [savingSiteSettings, wrapSavingSiteSettings] = useLoadingState();
    const updateSiteSettings = useEventCallback(async (input, toastInfos) => {
        return wrapSavingSiteSettings(async () => {
            await toast.catch(async () => {
                await apiClient.siteCustomization.updateSiteCustomizationById(
                    organizationId,
                    site.id,
                    {
                        ...siteSettings,
                        ...input,
                    }
                );
                toast.success(toastInfos.title, {
                    description: 'Your site changes will be reflected soon.',
                });
            }, 'Error while updating site settings');
        });
    });

    const value = React.useMemo(
        () => ({
            siteSettings,
            updateSiteSettings,
            savingSiteSettings,
        }),
        [updateSiteSettings, siteSettings, savingSiteSettings]
    );

    return <SiteSettingsContext.Provider value={value}>{children}</SiteSettingsContext.Provider>;
}

export function useSiteSettingsContext() {
    const context = React.useContext(SiteSettingsContext);
    if (!context) {
        throw new Error('useSiteSettingsContext must be used within a SiteSettingsProvider');
    }
    return context;
}
